/**
 * Cart storage utilities - uses localStorage for persistence across page refreshes
 */
export const CART_STORAGE_KEY = "cartItems";

export const getCartItems = (): any[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const setCartItems = (items: any[]) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  // Trigger storage event for components to update
  window.dispatchEvent(new Event("storage"));
};

export const clearCart = () => {
  localStorage.removeItem(CART_STORAGE_KEY);
  window.dispatchEvent(new Event("storage"));
};

const resetQuizCodeAllocation = () => {
  sessionStorage.removeItem("currentPerfumeCodes");
  sessionStorage.removeItem("quizCodeAllocationKey");
};

/**
 * Removes only the selected items from cart (used after partial checkout)
 * Returns the items that were checked out
 */
export const removeCheckedOutItems = (): any[] => {
  const selectedItemsJson = sessionStorage.getItem("checkoutSelectedItems");
  if (!selectedItemsJson) {
    // No selection means all items were checked out - clear everything
    const allItems = getCartItems();
    clearCart();
    sessionStorage.removeItem("checkoutSelectedItems");
    return allItems;
  }
  
  const selectedIds = new Set(JSON.parse(selectedItemsJson));
  const currentCart = getCartItems();
  const checkedOutItems = currentCart.filter(item => selectedIds.has(item.id));
  const remainingItems = currentCart.filter(item => !selectedIds.has(item.id));
  
  // Save remaining items back to cart
  setCartItems(remainingItems);
  
  // Clear the selection
  sessionStorage.removeItem("checkoutSelectedItems");
  
  return checkedOutItems;
};

/**
 * Clears all session data related to the current order.
 * Call this when an order is removed from cart to allow fresh selections.
 * NOTE: Cart items are now stored in localStorage, not sessionStorage
 */
export const resetCurrentOrderSession = () => {
  // Quiz data
  sessionStorage.removeItem("quizData");
  sessionStorage.removeItem("quizCurrentStep");
  sessionStorage.removeItem("quizFormData");
  
  // Selection data
  sessionStorage.removeItem("selectedOption");
  sessionStorage.removeItem("selectedPerfume");
  sessionStorage.removeItem("perfumes");
  resetQuizCodeAllocation();
  
  // Personalization data
  sessionStorage.removeItem("greetingMessage");
  sessionStorage.removeItem("photoPreview");
  
  // Order ID
  sessionStorage.removeItem("currentOrderId");
  
  // Edit mode flags
  sessionStorage.removeItem("editMode");
  sessionStorage.removeItem("editingCartItem");
  sessionStorage.removeItem("editingCartItemId");
  sessionStorage.removeItem("editSessionKey");
  sessionStorage.removeItem("editLockedCountry");
  sessionStorage.removeItem("originalPerfumes");
  sessionStorage.removeItem("originalQuizPreferences");
  sessionStorage.removeItem("justFinishedEditing");
  sessionStorage.removeItem("returnToQuiz");
  
  // Add more flow flags
  sessionStorage.removeItem("addMoreFlow");
  sessionStorage.removeItem("addMoreLockedCountry");
  sessionStorage.removeItem("quizLockedCountry");
  
  // Back from checkout flags
  sessionStorage.removeItem("backFromCheckout");
  sessionStorage.removeItem("viewingCartItemId");
  sessionStorage.removeItem("editingCurrentOrder");
  
  // Reset progress
  sessionStorage.removeItem("highestPhaseReached");
};
